import React, { useState, useEffect, useCallback } from 'react';
import { generateBIRFormPDF } from './generateDownloadableForms';
import { useActiveCompany } from 'stores/activeCompanyStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useCompanyServiceQueries } from 'services/company';
import { Company } from 'types/company';
import Button from 'components/Button';
import { FileText, Download, Info } from 'lucide-react';
import styles from './Forms.module.scss';

const FORMS = [
  {
    id: 'b1',
    name: 'Annex B-1 RR 11-2018',
    category: 'Sworn Declaration',
    description:
      'For Self-Employed and/or Engaged in the Practice of Profession with Several Income Payors',
  },
  {
    id: 'b2',
    name: 'Annex B-2 RR 11-2018',
    category: 'Sworn Declaration',
    description:
      'For Self-Employed and/or Engaged in the Practice of Profession with Lone Income Payor',
  },
  {
    id: 'b3',
    name: 'Annex B-3 RR 11-2018',
    category: 'Sworn Declaration',
    description: 'For Non-Individual Taxpayer with Several Income Payors',
  },
];

const FORM_FIELDS: Record<string, any[]> = {
  b1: [
    { key: 'taxpayer_name', label: 'Taxpayer Name', required: true },
    {
      key: 'tin',
      label: 'TIN',
      placeholder: '000-000-000-000',
      required: true,
    },
    { key: 'address', label: 'Taxpayer Address', required: true },
    { key: 'business_address', label: 'Business Address', required: true },
    { key: 'citizenship', label: 'Citizenship', required: true },
    { key: 'name_of_spouse', label: 'Name of Spouse', required: false },
    {
      key: 'busines_professional',
      label: 'Business/Profession',
      required: true,
    },
    {
      key: 'tax_rate',
      label: 'Tax Rate',
      type: 'select',
      options: [
        { label: 'Graduated Income Tax Rate', value: 'graduated_income' },
        { label: '8% IT Rate', value: 'eight_percent' },
      ],
      required: true,
    },
    { key: 'hereunto_day', label: 'Day (Signing)', required: true },
    { key: 'hereunto_month', label: 'Month (Signing)', required: true },
    { key: 'hereunto_year', label: 'Year (Signing)', required: true },
    { key: 'hereunto_location', label: 'Location (Signing)', required: true },
    { key: 'current_year', label: 'Current Year', required: true },
    { key: 'section', label: 'Section', required: true },
    {
      key: 'revenue_regulations_no',
      label: 'Revenue Regulations No.',
      required: true,
    },
  ],
  b2: [
    { key: 'taxpayer_name', label: 'Taxpayer Name', required: true },
    {
      key: 'tin',
      label: 'TIN',
      placeholder: '000-000-000-000',
      required: true,
    },
    { key: 'address', label: 'Taxpayer Address', required: true },
    { key: 'business_address', label: 'Business Address', required: true },
    { key: 'citizenship', label: 'Citizenship', required: true },
    { key: 'name_of_spouse', label: 'Name of Spouse', required: false },
    {
      key: 'busines_professional',
      label: 'Business/Profession',
      required: true,
    },
    {
      key: 'name_of_lone_payer',
      label: 'Name of Lone Income Payor',
      required: true,
    },
    { key: 'lone_payer_tin', label: 'Lone Payor TIN', required: true },
    { key: 'lone_payer_address', label: 'Lone Payor Address', required: true },
    {
      key: 'tax_rate',
      label: 'Tax Rate',
      type: 'select',
      options: [
        { label: 'Graduated Income Tax Rate', value: 'graduated_income' },
        { label: '8% IT Rate', value: 'eight_percent' },
      ],
      required: true,
    },
    { key: 'hereunto_day', label: 'Day (Signing)', required: true },
    { key: 'hereunto_month', label: 'Month (Signing)', required: true },
    { key: 'hereunto_year', label: 'Year (Signing)', required: true },
    { key: 'hereunto_location', label: 'Location (Signing)', required: true },
    { key: 'current_year', label: 'Current Year', required: true },
    { key: 'section', label: 'Section', required: true },
    {
      key: 'revenue_regulations_no',
      label: 'Revenue Regulations No.',
      required: false,
    },
  ],
  b3: [
    { key: 'taxpayer_name', label: 'Taxpayer Name', required: true },
    {
      key: 'tin',
      label: 'TIN',
      placeholder: '000-000-000-000',
      required: true,
    },
    { key: 'address', label: 'Address', required: true },
    { key: 'citizenship', label: 'Citizenship', required: true },
    {
      key: 'name_of_non_individual_payee',
      label: 'Name of Non-Individual Payee',
      required: true,
    },
    { key: 'hereunto_day', label: 'Day (Signing)', required: true },
    { key: 'hereunto_month', label: 'Month (Signing)', required: true },
    { key: 'hereunto_year', label: 'Year (Signing)', required: true },
    { key: 'hereunto_location', label: 'Location (Signing)', required: true },
    { key: 'current_year', label: 'Current Year', required: true },
    { key: 'section', label: 'Section', required: true },
    {
      key: 'revenue_regulations_no',
      label: 'Revenue Regulations No.',
      required: true,
    },
  ],
};

const FormsPage: React.FC = () => {
  const activeCompanySummary = useActiveCompany();
  const profile = useUserProfileStore((s) => s.profile);
  const companyService = useCompanyServiceQueries();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<(typeof FORMS)[0] | null>(
    null,
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fullCompany, setFullCompany] = useState<Company | null>(null);
  const [modalStep, setModalStep] = useState<'form' | 'preview'>('form');
  const [previewStatus, setPreviewStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<Record<string, string>>({
    taxpayer_name: '',
    address: '',
    tin: '',
    email: '',
    tel: '',
    tax_rate: '',
    citizenship: 'Filipino',
  });

  const initializedCompanyIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (
      activeCompanySummary?.id &&
      activeCompanySummary.id !== initializedCompanyIdRef.current
    ) {
      void companyService
        .getCompanyById(activeCompanySummary.id)
        .then((company) => {
          initializedCompanyIdRef.current = activeCompanySummary.id;
          setFullCompany(company);
          const now = new Date();
          const businessName =
            company.tradeName || company.businessStyle || company.name;
          setFormData({
            taxpayer_name: company.name,
            address: company.address || '',
            business_address: company.address || '',
            tin: company.tin || '',
            email: company.email || profile?.email || '',
            tel: company.tel || '',
            tax_rate: '',
            citizenship: 'Filipino',
            busines_professional: businessName,
            name_of_lone_payer: businessName,
            lone_payer_tin: company.tin || '',
            lone_payer_address: company.address || '',
            name_of_non_individual_payee: company.name,
            hereunto_day: now.getDate().toString(),
            hereunto_month: now.toLocaleString('default', { month: 'long' }),
            hereunto_year: now.getFullYear().toString().substring(2),
            hereunto_location: '',
            current_year: now.getFullYear().toString(),
            section: '',
            revenue_regulations_no: '11-2018',
          });
        });
    }
  }, [activeCompanySummary?.id, companyService, profile?.email]);

  const createPdfBytes = useCallback(
    async (formId: string, data: Record<string, string>) => {
      return generateBIRFormPDF(formId, data);
    },
    [],
  );

  const generatePreview = useCallback(
    async (formId: string, data: Record<string, string>) => {
      try {
        setPreviewStatus('loading');
        setErrorMessage(null);

        const pdfBytes = await createPdfBytes(formId, data);
        if (!pdfBytes) return;

        const blob = new Blob([pdfBytes.buffer as ArrayBuffer], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        setPdfUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
        setPreviewStatus('success');
      } catch (error) {
        setPreviewStatus('error');
        setErrorMessage(
          error instanceof Error ? error.message : 'Unknown error occurred',
        );
      }
    },
    [createPdfBytes],
  );

  const handleDownload = async () => {
    if (!selectedForm) return;
    try {
      const pdfBytes = await createPdfBytes(selectedForm.id, formData);
      if (!pdfBytes) return;

      const blob = new Blob([pdfBytes.buffer as ArrayBuffer], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedForm.name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!fullCompany) return;
    try {
      setIsSaving(true);
      await companyService.updateCompany(fullCompany.id, {
        tin: formData.tin,
        address: formData.address,
        name: formData.taxpayer_name,
        // Add other fields if necessary
      });
      // Optionally refresh fullCompany here
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && selectedForm && modalStep === 'preview') {
      const timer = setTimeout(() => {
        void generatePreview(selectedForm.id, formData);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isModalOpen, selectedForm, formData, modalStep, generatePreview]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedForm(null);
    setModalStep('form');
    setPdfUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, handleCloseModal]);

  const handleOpenForm = (formId: string) => {
    const form = FORMS.find((f) => f.id === formId);
    if (form) {
      setSelectedForm(form);
      setModalStep('form');
      setIsModalOpen(true);
    }
  };

  const handleNextStep = () => {
    if (!selectedForm) return;

    // Only validate the fields that are actually being shown in the missingFields list
    const missingInInput = missingFields.filter(
      (f) => f.required && !formData[f.key],
    );

    if (missingInInput.length > 0) {
      setErrorMessage(
        `Please fill in the required fields: ${missingInInput
          .map((m) => m.label)
          .join(', ')}`,
      );
      return;
    }

    setErrorMessage(null);
    setModalStep('preview');
  };

  const currentFormFields = selectedForm ? FORM_FIELDS[selectedForm.id] : [];
  const missingFields = currentFormFields.filter((field) => {
    const valueInCompany = fullCompany ? (fullCompany as any)[field.key] : null;
    return !valueInCompany || field.key === 'tax_rate'; // Always show tax_rate and citizenship for selection
  });

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Downloadable Forms</h1>
        <p>
          Browse and generate high-fidelity regulatory forms pre-filled with
          your business data.
        </p>
      </header>

      <div className={styles.formGrid}>
        {FORMS.map((form) => (
          <div
            key={form.id}
            className={styles.formCard}
            onClick={() => handleOpenForm(form.id)}
          >
            <div className={styles.cardBadge}>{form.category}</div>
            <div className={styles.cardIcon}>
              <FileText size={24} />
            </div>
            <div className={styles.cardContent}>
              <h3>{form.name}</h3>
              <p>{form.description}</p>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && selectedForm && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.selectionPanel}>
              <header className={styles.headerInfo}>
                <div className={styles.headerTitleRow}>
                  <h2>{selectedForm.name}</h2>
                </div>
                <span>Form Customization</span>
              </header>

              <div className={styles.descriptionSection}>
                <div className={styles.sectionTitle}>Form Description</div>

                <div className={styles.formDetailCard}>
                  <h3>{selectedForm.name}</h3>
                  <p>{selectedForm.description}</p>
                </div>

                <div className={styles.infoNote}>
                  <Info size={16} />
                  <p>
                    This form is automatically generated and pre-filled with
                    your active company profile data.
                  </p>
                </div>
              </div>

              <footer className={styles.sidebarFooter}>
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
              </footer>
            </div>

            <main className={styles.previewPanel}>
              {modalStep === 'form' ? (
                <div className={styles.formStepContainer}>
                  <div className={styles.formStepHeader}>
                    <h3>Required Information</h3>
                    <p>
                      Please provide the following details to complete the form
                      generation.
                    </p>
                  </div>

                  <div className={styles.inputForm}>
                    {errorMessage && (
                      <div className={styles.errorAlert}>
                        <Info size={18} />
                        <span>{errorMessage}</span>
                      </div>
                    )}
                    {missingFields.map((field) => (
                      <div key={field.key} className={styles.inputGroup}>
                        <label>
                          {field.label}
                          {field.required && (
                            <span
                              style={{ color: '#ef4444', marginLeft: '4px' }}
                            >
                              *
                            </span>
                          )}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            className={
                              field.required &&
                              !formData[field.key] &&
                              errorMessage
                                ? styles.inputError
                                : ''
                            }
                            value={formData[field.key] || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [field.key]: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Option</option>
                            {field.options.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className={
                              field.required &&
                              !formData[field.key] &&
                              errorMessage
                                ? styles.inputError
                                : ''
                            }
                            placeholder={field.placeholder || ''}
                            value={formData[field.key] || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [field.key]: e.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className={styles.formStepFooter}>
                    <Button
                      variant="outline"
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save to Profile'}
                    </Button>
                    <Button variant="primary" onClick={handleNextStep}>
                      Generate PDF Preview
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.previewHeader}>
                    <div className={styles.previewInfo}>
                      <h3>Form Preview</h3>
                      <p>Review the generated document before downloading.</p>
                    </div>
                    <div className={styles.previewActions}>
                      <Button
                        variant="outline"
                        onClick={() => setModalStep('form')}
                      >
                        Edit Info
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleDownload}
                        disabled={!pdfUrl}
                      >
                        <Download size={18} style={{ marginRight: '8px' }} />
                        Download PDF
                      </Button>
                    </div>
                  </div>

                  {previewStatus === 'loading' && (
                    <div className={styles.loaderWrapper}>
                      <div className={styles.spinner}></div>
                      Preparing Preview...
                    </div>
                  )}

                  {previewStatus === 'error' && (
                    <div className={styles.loaderWrapper}>
                      <div
                        style={{
                          color: '#ef4444',
                          textAlign: 'center',
                          padding: '2rem',
                        }}
                      >
                        <h3>Preview Failed</h3>
                        <p>
                          {errorMessage || 'Could not generate form preview.'}
                        </p>
                        <Button
                          variant="outline"
                          onClick={() =>
                            void generatePreview(selectedForm.id, formData)
                          }
                          style={{ marginTop: '1rem' }}
                        >
                          Retry
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className={styles.pdfWrapper}>
                    {pdfUrl && previewStatus === 'success' && (
                      <iframe
                        src={`${pdfUrl}#view=XYZ,400,842,1.25&toolbar=0&navpanes=0&scrollbar=0`}
                        className={styles.viewerWindow}
                        title="Form Preview"
                      />
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsPage;
